export const validateEmail = (email) => {
  if (!email) return 'Email is required';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return 'Invalid email format';
  return null;
};

export const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
  if (!/(?=.*[@$!%*?&])/.test(password)) return 'Password must contain at least one special character';
  return null;
};

// To handle optional phone numbers
export const validatePhone = (phone) => {
  // If phone is empty or just "+", it's optional - return null (valid)
  if (!phone || phone === '+') return null;
  
  // Enhanced phone regex to handle international formats better
  const phoneRegex = /^[+]?[1-9]\d{6,15}$/;
  if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
    return 'Invalid phone number format (7-16 digits)';
  }
  
  return null;
};

export const validateOTP = (otp) => {
  if (!otp) return 'OTP is required';
  if (!/^\d{6}$/.test(otp)) return 'OTP must be 6 digits';
  return null;
};

export const formatAPIError = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Additional useful validation functions
export const validateName = (name) => {
  if (!name) return 'Name is required';
  if (name.length < 2) return 'Name must be at least 2 characters';
  if (name.length > 50) return 'Name must be less than 50 characters';
  if (!/^[a-zA-Z\s]+$/.test(name)) return 'Name can only contain letters and spaces';
  return null;
};

export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) return 'Password confirmation is required';
  if (password !== confirmPassword) return 'Passwords do not match';
  return null;
};

export const validateSecurityAnswer = (answer) => {
  if (!answer) return 'Security answer is required';
  if (answer.length < 2) return 'Security answer must be at least 2 characters';
  if (answer.length > 100) return 'Security answer must be less than 100 characters';
  return null;
};

// Utility function for form validation
export const hasValidationErrors = (errors) => {
  return Object.values(errors).some(error => error !== null && error !== '');
};

export const validateDateOfBirth = (dob) => {
  if (!dob) return 'Date of Birth is required';
  
  const dobDate = new Date(dob);
  const today = new Date();
  
  if (dobDate > today) return 'Date of Birth cannot be in the future';
  
  const age = today.getFullYear() - dobDate.getFullYear();
  if (age < 12) return 'You must be at least 12 years old';
  if (age > 120) return 'Please enter a valid date of birth';
  
  return null;
};

// Helper function for email validation (used in education validation)
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper function for URL validation
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Single validateEducation function (removed duplicate)
export const validateEducation = (data) => {
  const errors = {};

  // Required fields
  if (!data.institution?.trim()) {
    errors.institution = 'Institution name is required';
  } else if (data.institution.length > 100) {
    errors.institution = 'Institution name must be less than 100 characters';
  }

  if (!data.degree?.trim()) {
    errors.degree = 'Degree/course name is required';
  } else if (data.degree.length > 100) {
    errors.degree = 'Degree must be less than 100 characters';
  }

  if (!data.startDate) {
    errors.startDate = 'Start date is required';
  } else {
    const startDate = new Date(data.startDate);
    const today = new Date();
    if (startDate > today) {
      errors.startDate = 'Start date cannot be in the future';
    }
  }

  // Verifier email validation (required)
  if (!data.verifierEmail?.trim()) {
    errors.verifierEmail = 'Institution verification email is required';
  } else if (!isValidEmail(data.verifierEmail)) {
    errors.verifierEmail = 'Please enter a valid email address';
  } else if (!data.verifierEmail.includes('university') && 
             !data.verifierEmail.includes('college') && 
             !data.verifierEmail.includes('institute') &&
             !data.verifierEmail.endsWith('.edu')) {
    errors.verifierEmail = 'Please provide an institutional email address';
  }

  // Date validation
  if (data.startDate && data.endDate && !data.isOngoing) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (start > end) {
      errors.endDate = 'End date must be after start date';
    }
  }

  if (data.endDate && !data.isOngoing) {
    const endDate = new Date(data.endDate);
    const today = new Date();
    if (endDate > today) {
      errors.endDate = 'End date cannot be in the future unless ongoing';
    }
  }

  // URL validation for transcript
  if (data.transcriptUrl && !isValidUrl(data.transcriptUrl)) {
    errors.transcriptUrl = 'Please enter a valid URL';
  }

  // Roll number validation
  if (data.rollNumber && data.rollNumber.length > 50) {
    errors.rollNumber = 'Roll number must be less than 50 characters';
  }

  // Grades validation
  if (data.grades && data.grades.length > 20) {
    errors.grades = 'Grades must be less than 20 characters';
  }

  return errors;
};

export const validateEducationList = (educationList) => {
  if (!Array.isArray(educationList)) {
    return { general: 'Education list must be an array' };
  }
  
  const errors = {};
  let hasErrors = false;
  
  educationList.forEach((edu, index) => {
    const eduErrors = validateEducation(edu);
    if (Object.keys(eduErrors).length > 0) {
      errors[index] = eduErrors;
      hasErrors = true;
    }
  });
  
  return hasErrors ? errors : {};
};

export const validateEmployment = (data) => {
  const errors = {};

  // Required fields
  if (!data.company?.trim()) {
    errors.company = 'Company name is required';
  } else if (data.company.length > 100) {
    errors.company = 'Company name must be less than 100 characters';
  }

  if (!data.jobTitle?.trim()) {
    errors.jobTitle = 'Job title is required';
  } else if (data.jobTitle.length > 100) {
    errors.jobTitle = 'Job title must be less than 100 characters';
  }

  if (!data.startDate) {
    errors.startDate = 'Start date is required';
  } else {
    const startDate = new Date(data.startDate);
    const today = new Date();
    if (startDate > today && !data.isOngoing) {
      errors.startDate = 'Start date cannot be in the future';
    }
  }

  // Verifier email validation (required but flexible)
  if (!data.verifierEmail?.trim()) {
    errors.verifierEmail = 'Verifier email is required';
  } else if (!isValidEmail(data.verifierEmail)) {
    errors.verifierEmail = 'Please enter a valid email address';
  }

  // Date validation
  if (data.startDate && data.endDate && !data.isOngoing) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (start > end) {
      errors.endDate = 'End date must be after start date';
    }
  }

  if (data.endDate && !data.isOngoing) {
    const endDate = new Date(data.endDate);
    const today = new Date();
    if (endDate > today) {
      errors.endDate = 'End date cannot be in the future unless ongoing';
    }
  }

  // Optional fields validation
  if (data.responsibilities && data.responsibilities.length > 1000) {
    errors.responsibilities = 'Responsibilities must be less than 1000 characters';
  }

  if (data.reasonForLeaving && data.reasonForLeaving.length > 200) {
    errors.reasonForLeaving = 'Reason for leaving must be less than 200 characters';
  }

  return errors;
};

export const validateReference = (data) => {
  const errors = {};

  // Required fields
  if (!data.name?.trim()) {
    errors.name = 'Full name is required';
  } else if (data.name.length > 100) {
    errors.name = 'Name must be less than 100 characters';
  }

  if (!data.email?.trim()) {
    errors.email = 'Email address is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Optional field validation
  if (data.contactNumber && data.contactNumber.length > 20) {
    errors.contactNumber = 'Contact number must be less than 20 characters';
  }

  if (data.designation && data.designation.length > 100) {
    errors.designation = 'Designation must be less than 100 characters';
  }

  return errors;
};
